import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination } from 'swiper/modules';
import { useScreenSize } from '@/hooks';
import { fetchBanners } from '@/services/api/bannerService';
import 'swiper/css';
import 'swiper/css/pagination';
import styles from './CasinoBannerSlider.module.css';

/**
 * CasinoBannerSlider Component
 * Banner slider for Casino page using Swiper
 * Shows 2 cards on desktop, 1 card on mobile
 * Supports overlay text (site name, etc.)
 * Can fetch banners from API or use provided banners
 */

// Default banner images from casino-carousel directory
const DEFAULT_CASINO_BANNERS = [
  {
    id: 1,
    image: '/assets/images/casino-carousel/first.webp',
    title: 'Welcome to Casino',
    subtitle: 'Play Now',
    siteName: 'JACKOPOT'
  },
  {
    id: 2,
    image: '/assets/images/casino-carousel/second.webp',
    title: 'Live Casino',
    subtitle: 'Experience Real Dealers',
    siteName: 'JACKOPOT'
  },
  {
    id: 3,
    image: '/assets/images/casino-carousel/third.webp',
    title: 'Popular Slots',
    subtitle: 'Spin & Win',
    siteName: 'JACKOPOT'
  },
  {
    id: 4,
    image: '/assets/images/casino-carousel/fourth.webp',
    title: 'Game Shows',
    subtitle: 'Win Big',
    siteName: 'JACKOPOT'
  },
  {
    id: 5,
    image: '/assets/images/casino-carousel/fifth.webp',
    title: 'Crash Games',
    subtitle: 'Fast & Fun',
    siteName: 'JACKOPOT'
  }
];

const CasinoBannerSlider = ({ banners: providedBanners = null, useApi = false }) => {
  const { isMobile } = useScreenSize();

  // Fetch banners from API if useApi is true
  const { data: apiBanners = [], isLoading } = useQuery({
    queryKey: ['casinoBanners'],
    queryFn: fetchBanners,
    enabled: useApi,
    staleTime: 300000, // 5 minutes cache
    retry: 2,
  });

  // Determine which banners to use
  const banners = providedBanners || (useApi && apiBanners.length > 0 
    ? apiBanners.map(banner => ({
        id: banner.id,
        image: banner.image,
        title: banner.type || 'Casino',
        subtitle: 'Play Now',
        siteName: 'JACKOPOT'
      }))
    : DEFAULT_CASINO_BANNERS);

  // Don't render if loading and no fallback
  if (isLoading && useApi && !providedBanners && apiBanners.length === 0) {
    return null;
  }

  return (
    <div className={styles.bannerSliderContainer}>
      <Swiper
        modules={[Autoplay, Pagination]}
        spaceBetween={16}
        slidesPerView={isMobile ? 1 : 2}
        slidesPerGroup={1}
        autoplay={{
          delay: 5000,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        }}
        pagination={{
          clickable: true,
          dynamicBullets: true,
        }}
        loop={banners.length > (isMobile ? 1 : 2)}
        speed={800}
        className={styles.swiper}
      >
        {banners.map((banner) => (
          <SwiperSlide key={banner.id} className={styles.slide}>
            <div className={styles.bannerCard}>
              <img
                src={banner.image}
                alt={banner.title || 'Casino Banner'}
                className={styles.bannerImage}
                onError={(e) => {
                  e.target.src = '/assets/images/casino-carousel/first.webp';
                }}
              />
              {/* Overlay with gradient */}
              <div className={styles.bannerOverlay}></div>
              
              {/* Content Overlay */}
              <div className={styles.bannerContent}>
                {banner.siteName && (
                  <div className={styles.siteName}>{banner.siteName}</div>
                )}
                {banner.title && (
                  <h2 className={styles.bannerTitle}>{banner.title}</h2>
                )}
                {banner.subtitle && (
                  <p className={styles.bannerSubtitle}>{banner.subtitle}</p>
                )}
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

export default CasinoBannerSlider;
